/**
 * SCM12 — RFQ-response PDF classifier.
 *
 * Pure heuristic detector that answers "is this freshly-uploaded document
 * a vendor's response to one of our RFQs?". Runs on the pre-extracted
 * plain-text (`SiteDocument.textContent`) — no network calls, no model
 * inference — so it can be invoked inline from the upload pipeline without
 * blocking or failing the upload when offline.
 *
 * Detection strategy (high-precision over high-recall): we only flip the
 * `isRfqResponse` flag when **at least two independent signals** light up,
 * and at least one of them is "vendor-fill" evidence. The thresholds were
 * tuned against the SCM11 template (`RFQ-<id>` header + vendor-fillable
 * Price / Lead-time / Validity / Payment-terms / Freight-terms / Currency
 * sections) plus the seven vendor templates SCM8 already supports
 * (Hitachi Energy, GE Vernova, Siemens Energy, NuScale, Prysmian, Tesla
 * Megapack, Cummins). False positives on a generic feasibility PDF would
 * fire the auto-extractor on the wrong shape and pollute the Quote ledger
 * — far worse than a false negative, which simply leaves the user to
 * manually multi-select against the BoM (the existing SCM8 affordance).
 *
 * Signals
 * -------
 *   S1 — RFQ identifier present: `RFQ-`, `RFQ #`, `RFQ ID:` etc. (strong)
 *   S2 — "Quote for RFQ" / "Response to RFQ" prose anywhere in the body.
 *   S3 — at least one **labeled price** field (Unit Price / Total Price /
 *        Grand Total / Net Price per unit) — i.e. the same labels SCM8's
 *        defined-pattern tier looks for. Vendor-fill evidence.
 *   S4 — at least one **labeled lead-time** field (Lead time / Delivery /
 *        Estimated delivery). Vendor-fill evidence.
 *   S5 — at least one **labeled commercial term** (Payment terms /
 *        Validity / Incoterm / Currency / Freight terms). Vendor-fill.
 *   S6 — vendor template fingerprint (matches the SCM8 template list).
 *        Weak signal on its own, useful as a tiebreaker.
 *
 * Decision rule:
 *   - `vendorFillSignals` = count of S3..S5 that hit.
 *   - `rfqSignals` = count of S1, S2 that hit.
 *   - Classify as RFQ response when
 *       (`vendorFillSignals` ≥ 2)
 *       OR (`vendorFillSignals` ≥ 1 AND `rfqSignals` ≥ 1)
 *
 * Output: a tagged record carrying the boolean verdict, the per-signal
 * breakdown (for the UI to surface "why this was auto-routed"), an
 * approximate RFQ id when extractable, and a 0..1 confidence the upload
 * pipeline can use to gate auto-routing vs. surface a "review and confirm"
 * affordance.
 */

export interface RfqClassification {
  /** Final verdict — true when the doc should be auto-routed to extractQuoteFromPdf. */
  isRfqResponse: boolean;
  /** 0..1 — combined evidence strength. `>=0.8` qualifies as "high confidence". */
  confidence: number;
  /** Extracted RFQ identifier (e.g. `RFQ-2026-042`) when present. */
  rfqId?: string;
  /** Per-signal breakdown — useful for the UI ("why was this auto-detected?"). */
  signals: {
    rfqIdentifier: boolean;
    rfqResponsePhrase: boolean;
    labeledPrice: boolean;
    labeledLeadTime: boolean;
    labeledCommercialTerm: boolean;
    vendorTemplateFingerprint: boolean;
  };
  /** Sum of "vendor fill" signals (price + lead + commercial). */
  vendorFillScore: number;
  /** Sum of explicit RFQ-context signals (identifier + response prose). */
  rfqContextScore: number;
}

/** Default minimum confidence above which the pipeline auto-routes. */
export const RFQ_AUTO_ROUTE_CONFIDENCE_THRESHOLD = 0.5;

const RFQ_ID_PATTERN =
  /\bRFQ\s*(?:#|id|number|no\.?|:)?\s*[-]?\s*([A-Z]{0,4}[-]?\d{2,4}[-]?\d{1,6}(?:[-]?[A-Z0-9]{1,8})?)\b/i;

const RFQ_RESPONSE_PHRASE_PATTERN =
  /\b(?:quote\s+for\s+RFQ|response\s+to\s+RFQ|in\s+reply\s+to\s+RFQ|reply\s+to\s+(?:your\s+)?RFQ|our\s+response\s+to\s+(?:your\s+)?RFQ|(?:bid|quotation)\s+for\s+RFQ)\b/i;

const LABELED_PRICE_PATTERN =
  /\b(?:unit\s+price|price\s+per\s+unit|net\s+price\s+per\s+unit|unit\s+cost|price\s+\(each\)|total\s+price|total\s+cost|grand\s+total|extended\s+price)\b/i;

const LABELED_LEAD_TIME_PATTERN =
  /\b(?:lead\s+time|delivery(?:\s+lead)?|estimated\s+delivery|delivery\s+schedule)\b/i;

const LABELED_COMMERCIAL_TERM_PATTERN =
  /\b(?:payment\s+terms?|terms?\s+of\s+payment|incoterms?|freight\s+terms?|shipping\s+terms?|delivery\s+terms?|validity|quote\s+valid\s+for|offer\s+valid\s+for|currency)\b/i;

/**
 * Vendor template fingerprints — matches the seven SCM8-supported
 * templates. The Hitachi Energy / GE Vernova / etc. brand strings are
 * stable identifiers; a stand-alone mention without other signals isn't
 * enough to classify (handled by the AND rule above) but bumps confidence.
 */
const VENDOR_TEMPLATE_PATTERNS: RegExp[] = [
  /\bHitachi\s+Energy\b/i,
  /\bGE\s+Vernova\b/i,
  /\bSiemens\s+Energy\b/i,
  /\bNuScale\b/i,
  /\bPrysmian\b/i,
  /\bTesla\s+Megapack\b/i,
  /\bCummins\b/i,
];

/**
 * Classify a freshly-uploaded document as RFQ response or not.
 *
 * @param text  Pre-extracted plain text of the document (typically
 *              `SiteDocument.textContent`). Empty / undefined inputs
 *              return a negative verdict with confidence 0.
 * @param hints Optional caller-supplied hints. `fileName` is consulted
 *              for boost-only signals (`RFQ-2026-042-response.pdf`),
 *              never as the sole basis for classification.
 */
export function classifyRfqResponse(
  text: string | null | undefined,
  hints: { fileName?: string } = {},
): RfqClassification {
  const body = typeof text === 'string' ? text : '';
  const fileName = hints.fileName ?? '';

  // ── Signal extraction ────────────────────────────────────────────
  const rfqIdMatch = RFQ_ID_PATTERN.exec(body) ?? RFQ_ID_PATTERN.exec(fileName);
  const hasRfqId = rfqIdMatch != null;
  const hasResponsePhrase = RFQ_RESPONSE_PHRASE_PATTERN.test(body);
  const hasLabeledPrice = LABELED_PRICE_PATTERN.test(body);
  const hasLabeledLeadTime = LABELED_LEAD_TIME_PATTERN.test(body);
  const hasLabeledCommercialTerm = LABELED_COMMERCIAL_TERM_PATTERN.test(body);
  const hasVendorFingerprint = VENDOR_TEMPLATE_PATTERNS.some((p) => p.test(body));

  const signals = {
    rfqIdentifier: hasRfqId,
    rfqResponsePhrase: hasResponsePhrase,
    labeledPrice: hasLabeledPrice,
    labeledLeadTime: hasLabeledLeadTime,
    labeledCommercialTerm: hasLabeledCommercialTerm,
    vendorTemplateFingerprint: hasVendorFingerprint,
  };

  const vendorFillScore =
    (hasLabeledPrice ? 1 : 0) +
    (hasLabeledLeadTime ? 1 : 0) +
    (hasLabeledCommercialTerm ? 1 : 0);
  const rfqContextScore = (hasRfqId ? 1 : 0) + (hasResponsePhrase ? 1 : 0);

  // ── Decision rule (see header docstring) ────────────────────────
  const isRfqResponse =
    vendorFillScore >= 2 || (vendorFillScore >= 1 && rfqContextScore >= 1);

  // ── Confidence scoring ──────────────────────────────────────────
  // Five "strong" inputs (vendorFill ×3 + rfqContext ×2). The vendor
  // fingerprint contributes a small additive boost (max +0.1) so a
  // labeled-price-only doc bumps from 0.2 → 0.3 when on a known template,
  // but a fingerprint alone can never push a negative to positive.
  const baseConfidence =
    (vendorFillScore * 0.2) + (rfqContextScore * 0.2);
  const fingerprintBoost = hasVendorFingerprint ? 0.1 : 0;
  const confidence = Math.min(1, Math.max(0, baseConfidence + fingerprintBoost));

  return {
    isRfqResponse,
    confidence,
    rfqId: rfqIdMatch ? rfqIdMatch[1] : undefined,
    signals,
    vendorFillScore,
    rfqContextScore,
  };
}

/**
 * Convenience wrapper — returns `true` only when the classifier wants
 * the pipeline to auto-route (i.e. positive verdict AND confidence
 * above the auto-route threshold).
 */
export function shouldAutoRouteAsRfqResponse(
  classification: RfqClassification,
  threshold: number = RFQ_AUTO_ROUTE_CONFIDENCE_THRESHOLD,
): boolean {
  return classification.isRfqResponse && classification.confidence >= threshold;
}

export const __test__ = {
  RFQ_ID_PATTERN,
  RFQ_RESPONSE_PHRASE_PATTERN,
  LABELED_PRICE_PATTERN,
  LABELED_LEAD_TIME_PATTERN,
  LABELED_COMMERCIAL_TERM_PATTERN,
  VENDOR_TEMPLATE_PATTERNS,
};
